import React from "react";
import { Link } from "react-router-dom";
import { CreditCard, FileText, UtensilsCrossed, BarChart3, BookOpen, Image as ImageIcon, PenTool, Video, Printer, QrCode, Sparkles, ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useShowcase } from "@/components/Showcase";
import { mediaUrl } from "@/lib/download";
import { track } from "@/lib/funnel";

const PRODUCTS = [
  { kind: "logo", icon: PenTool, ar: ["لوقو بهوية سعودية", "٣ مقترحات ملوّنة تختار منها"], en: ["Saudi-identity logo", "3 colour concepts to pick from"] },
  { kind: "business_card", icon: CreditCard, ar: ["بطاقة عمل vCard", "QR يحفظ جهة اتصالك بنقرة"], en: ["vCard business card", "QR saves your contact in a tap"] },
  { kind: "menu", icon: UtensilsCrossed, ar: ["منيو وقائمة أسعار", "أصنافك وأسعارك منسّقة"], en: ["Menu & price list", "Your items and prices, typeset"] },
  { kind: "brochure", icon: BookOpen, ar: ["بروشور ثلاثي", "PDF بخطوط الطيّ"], en: ["Tri-fold brochure", "PDF with fold lines"] },
  { kind: "infographic", icon: BarChart3, ar: ["إنفوجرافيك", "نقاطك في بطاقات واضحة"], en: ["Infographic", "Your points as clear cards"] },
  { kind: "cover", icon: ImageIcon, ar: ["غلاف وبانر", "بمقاس كل منصة"], en: ["Cover & banner", "Sized for every platform"] },
  { kind: "video", icon: Video, ar: ["فيديو إعلاني 15 ث", "مقدّم سعودي أو أنيميشن"], en: ["15-second ad video", "Saudi presenter or animation"] },
  { kind: "text", icon: FileText, ar: ["منشورات وسكربتات", "مجانًا بلا رصيد"], en: ["Posts & scripts", "Free, no credits"], free: true },
];

/** معرض أعمال مصغّر للتوليد الفردي: بطاقات صغيرة بصورة حقيقية (إن وُجدت عينة مميّزة من النوع) — صف أفقي على الجوال حتى يصل الزائر سريعًا إلى «المسارات الثلاثة» */
export const IndividualProducts = () => {
  const { t } = useLang();
  const items = useShowcase() || [];
  const sampleFor = (kind) => items.find((it) => it.item_kind === kind && it.type === "image");
  return (
    <section id="individual" className="py-12 sm:py-16 px-6 max-w-7xl mx-auto" data-testid="individual-products-section">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs rounded-full border border-accent/40 bg-accent/10 text-accent px-3 py-1 font-bold"><Sparkles className="w-3.5 h-3.5" /> {t("خدمات حسب الطلب — بدون اشتراك", "On-demand services — no subscription")}</span>
          <h2 className="text-base md:text-lg font-black mt-2 leading-snug">{t("اختر ما تحتاجه الآن: لوقو، بطاقة عمل، منيو، بروشور… جاهز للطباعة", "Pick what you need now: logo, business card, menu, brochure… print-ready")}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t("تكتب بياناتك فتُنسَّق داخل التصميم بنص عربي مدقَّق — تبدأ من 8 ريال، والنصوص مجانية.", "Type your details and they are typeset inside the design — from SAR 8, texts free.")}</p>
        </div>
        <Link to="/register" onClick={() => track("cta_click", "individual_products")} className="inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline" data-testid="individual-products-cta">
          {t("ابدأ بخدمة مجانية", "Start with a free service")} <ArrowLeft className="w-4 h-4" />
        </Link>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory -mx-6 px-6 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-4 lg:grid-cols-8 sm:overflow-visible" data-testid="individual-products-grid">
        {PRODUCTS.map((p, i) => {
          const [title, desc] = t(p.ar, p.en);
          const sm = sampleFor(p.kind);
          return (
            <Link key={p.kind} to={`/register?kind=${p.kind}`} onClick={() => track("cta_click", `product_${p.kind}`)}
              className="group shrink-0 snap-start w-[46vw] max-w-[200px] sm:w-auto sm:max-w-none glass rounded-xl border border-border/70 hover:border-primary/50 hover:-translate-y-0.5 transition-all overflow-hidden"
              data-testid={`individual-product-${i}`}>
              {sm ? (
                <img src={mediaUrl(sm.url)} alt={title} loading="lazy" className="w-full aspect-[4/3] lg:aspect-square object-cover" />
              ) : (
                <div className="w-full aspect-[4/3] lg:aspect-square bg-gradient-to-br from-primary/15 via-secondary to-accent/10 flex items-center justify-center">
                  <p.icon className="w-7 h-7 text-primary/80 group-hover:scale-110 transition-transform" />
                </div>
              )}
              <div className="p-2.5">
                <div className="flex items-start gap-1.5">
                  <p.icon className="w-3.5 h-3.5 text-primary shrink-0" />
                  <h3 className="font-bold text-xs sm:text-sm leading-tight line-clamp-2">{title}</h3>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{desc}</p>
                {p.free && <span className="inline-block mt-1 text-[10px] font-bold rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2 py-0.5">{t("مجانًا", "Free")}</span>}
              </div>
            </Link>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><Printer className="w-3.5 h-3.5" /> {t("PDF جاهز للطباعة", "Print-ready PDF")}</span>
        <span className="inline-flex items-center gap-1.5"><QrCode className="w-3.5 h-3.5" /> {t("QR وبطاقة رقمية تحفظ جهة الاتصال", "QR + digital card that saves the contact")}</span>
        <span className="inline-flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> {t("تعديل البيانات لاحقًا بلا تكلفة", "Edit details later at no cost")}</span>
      </div>
    </section>
  );
};
